import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { Experiment, ExperimentClient, Variant, Variants } from '@amplitude/experiment-js-client';

interface AmplitudeExperimentContextType {
    getVariant: (flagKey: string) => Variant | undefined;
    getVariantValue: (flagKey: string, fallback?: string) => string;
    getAllVariants: () => Variants;
    trackExposure: (flagKey: string) => void;
    refetchVariants: () => Promise<void>;
    isReady: boolean;
    isLoading: boolean;
}

const AmplitudeExperimentContext = createContext<AmplitudeExperimentContextType | undefined>(undefined);

interface AmplitudeExperimentProviderProps {
    children: ReactNode;
}

export const AmplitudeExperimentProvider: React.FC<AmplitudeExperimentProviderProps> = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const clientRef = useRef<ExperimentClient | null>(null);
    const exposedFlags = useRef<Set<string>>(new Set());

    useEffect(() => {
        const deploymentKey = process.env.REACT_APP_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY;
        if (!deploymentKey) {
            console.warn('AmplitudeExperimentProvider: No deployment key found. Set REACT_APP_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY in .env');
            setIsLoading(false);
            return;
        }

        const client = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
            // Disable automatic $exposure tracking — we fire it manually in
            // useExperiment/useExperimentValue hooks via useEffect so exposure
            // is tracked only when the user actually sees the variant, not when
            // getActiveExperiments() reads variants for analytics enrichment.
            automaticExposureTracking: false,
        });
        clientRef.current = client;
        // Expose client on window so the functional analytics module can read
        // active variants without React hooks (see getActiveExperiments()).
        (window as any).__AMPLITUDE_EXPERIMENT_CLIENT__ = client;

        client.fetch()
            .then(() => {
                setIsReady(true);
            })
            .catch((err) => {
                console.error('AmplitudeExperimentProvider: Failed to fetch variants', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const getVariant = useCallback((flagKey: string): Variant | undefined => {
        return clientRef.current?.variant(flagKey);
    }, []);

    const getVariantValue = useCallback((flagKey: string, fallback: string = 'control'): string => {
        const variant = clientRef.current?.variant(flagKey);
        return variant?.value ?? fallback;
    }, []);

    const getAllVariants = useCallback((): Variants => {
        return clientRef.current?.all() ?? {};
    }, []);

    const trackExposure = useCallback((flagKey: string) => {
        if (!clientRef.current || exposedFlags.current.has(flagKey)) return;
        exposedFlags.current.add(flagKey);
        clientRef.current.exposure(flagKey);
    }, []);

    const refetchVariants = useCallback(async () => {
        if (!clientRef.current) return;
        setIsLoading(true);
        try {
            await clientRef.current.fetch();
            setIsReady(true);
        } catch (err) {
            console.error('AmplitudeExperimentProvider: Failed to refetch variants', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const value = useMemo<AmplitudeExperimentContextType>(() => ({
        getVariant,
        getVariantValue,
        getAllVariants,
        trackExposure,
        refetchVariants,
        isReady,
        isLoading,
    }), [getVariant, getVariantValue, getAllVariants, trackExposure, refetchVariants, isReady, isLoading]);

    return (
        <AmplitudeExperimentContext.Provider value={value}>
            {children}
        </AmplitudeExperimentContext.Provider>
    );
};

export const useAmplitudeExperiment = (): AmplitudeExperimentContextType => {
    const context = useContext(AmplitudeExperimentContext);
    if (!context) {
        throw new Error('useAmplitudeExperiment must be used within AmplitudeExperimentProvider');
    }
    return context;
};
